import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { Search, CheckCircle, Clock, Package } from 'lucide-react';

const STATUS_MAP = {
  pending: { label: 'Menunggu', badge: 'badge-gray', icon: <Clock size={12} /> },
  proses: { label: 'Diproses', badge: 'badge-amber', icon: <Package size={12} /> },
  distribusi: { label: 'Dikirim', badge: 'badge-blue', icon: '🚚' },
  selesai: { label: 'Diterima', badge: 'badge-green', icon: <CheckCircle size={12} /> },
};

export default function DonorListPage() {
  const [donors, setDonors] = useState([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const unsub = onSnapshot(query(collection(db, 'donors'), orderBy('name', 'asc')), snap => {
      setDonors(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, []);

  const filtered = donors.filter(d => {
    const matchSearch = d.name?.toLowerCase().includes(search.toLowerCase()) ||
      d.animal?.toLowerCase().includes(search.toLowerCase()) ||
      d.address?.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === 'all' || d.distributionStatus === filter;
    return matchSearch && matchFilter;
  });

  const counts = {
    all: donors.length,
    selesai: donors.filter(d => d.distributionStatus === 'selesai').length,
    distribusi: donors.filter(d => d.distributionStatus === 'distribusi').length,
    pending: donors.filter(d => !d.distributionStatus || d.distributionStatus === 'pending').length,
  };

  return (
    <div style={{ paddingTop: 80, minHeight: '100vh', background: '#f4fbf6' }}>
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '0 1.5rem 4rem' }}>

        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1 style={{ fontSize: '2rem', color: 'var(--green-900)' }}>Daftar Pengkurban</h1>
          <p style={{ color: 'var(--text-muted)', marginTop: 6 }}>
            Status distribusi daging untuk setiap pengkurban yang terdaftar.
          </p>
        </div>

        {/* Summary */}
        <div className="stats-grid" style={{ marginBottom: '1.5rem' }}>
          {[
            { label: 'Total Pengkurban', value: counts.all, color: 'var(--green-800)' },
            { label: 'Sudah Diterima', value: counts.selesai, color: '#16a34a' },
            { label: 'Sedang Dikirim', value: counts.distribusi, color: '#0369a1' },
            { label: 'Menunggu', value: counts.pending, color: '#d97706' },
          ].map((s, i) => (
            <div key={i} className="stat-card">
              <div className="stat-label">{s.label}</div>
              <div className="stat-value" style={{ color: s.color }}>{s.value}</div>
            </div>
          ))}
        </div>

        {/* Search & filter */}
        <div style={{ display: 'flex', gap: 12, marginBottom: '1.25rem', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
            <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
            <input className="form-input" placeholder="Cari nama, hewan, atau alamat..."
              style={{ paddingLeft: 38 }} value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {[
              { key: 'all', label: 'Semua' },
              { key: 'selesai', label: '✅ Diterima' },
              { key: 'distribusi', label: '🚚 Dikirim' },
              { key: 'pending', label: '⏳ Menunggu' },
            ].map(f => (
              <button key={f.key} className="btn btn-sm"
                style={{ background: filter === f.key ? 'var(--green-700)' : '#fff', color: filter === f.key ? '#fff' : 'var(--text)', border: '1.5px solid var(--border)' }}
                onClick={() => setFilter(f.key)}>{f.label}</button>
            ))}
          </div>
        </div>

        {/* List */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
              {search ? `Tidak ada pengkurban bernama "${search}"` : 'Belum ada data pengkurban.'}
            </div>
          ) : (
            filtered.map((donor, i) => {
              const status = STATUS_MAP[donor.distributionStatus || 'pending'];
              return (
                <div key={donor.id} className="card" style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px' }}>
                  <div style={{
                    width: 42, height: 42, borderRadius: '50%',
                    background: donor.distributionStatus === 'selesai' ? 'var(--green-700)' : 'var(--green-100)',
                    color: donor.distributionStatus === 'selesai' ? '#fff' : 'var(--green-700)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, fontSize: 16, flex: 'none'
                  }}>
                    {(i + 1).toString().padStart(2, '0')}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--green-900)', marginBottom: 2 }}>{donor.name}</div>
                    <div style={{ fontSize: 13, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {donor.animalType === 'sapi' ? '🐄' : '🐏'} {donor.animalName || 'Hewan Qurban'} · {donor.address || 'Alamat belum diisi'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                    <span className={`badge ${status.badge}`} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                      {status.icon} {status.label}
                    </span>
                    {donor.distributionDate && (
                      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                        {new Date(donor.distributionDate?.toDate?.() || donor.distributionDate).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
